import {
	ConfirmationDialog,
	ContextMenu,
	ContextMenuItem,
	ContextMenuLinkItem,
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { TFunction } from "i18next";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PermissionName } from "../../../shared/types/permission";
import { Address } from "../../../shared/ui/Address";
import { TagMultiSelectField, TagSingleSelectField } from "../../../shared/ui/TagSelectField";
import { companyService } from "../shared/api/companyService";
import { useSidebarData } from "../shared/api/sidebarService";
import { TenantArchiveDrawer } from "../shared/tenantArchive/TenantArchiveDrawer";
import { useArchiveByTenantHandler } from "../shared/tenantArchive/useRestoreByTenantHandler";
import {
	CarFleetStatus,
	CompanyCreateRequest,
	ICompanyFormState,
	ICompanyIndexResource,
	ICompanyRequest,
	TagOptionResource,
} from "../shared/types";
import { CarFleetStatusSelect } from "./CarFleetStatusSelect";
import { CompanyListBulkActions } from "./CompanyListBulkActions";
import { CompanyUpdateDrawer } from "./CompanyUpdateDrawer";
import { CreateCompanyDrawer } from "./create/CreateCompanyDrawer";
import { useCompany } from "./create/useCompany";
import { EventPrioritySelect } from "./EventPrioritySelect";

type TagFilterValue = TagOptionResource | TagOptionResource[] | null;

const getFilterValueForTag = (value: TagFilterValue): string | null => {
	if (!value) {
		return null;
	} else {
		return Array.isArray(value) ? value.map((tag) => tag.name).join(", ") : value.name;
	}
};

function validateTagFilter(operator: string, value: TagFilterValue, t: TFunction) {
	switch (operator) {
		case "=":
		case "!=":
			return value ? null : t("Field is required");
		case "IN_RELATION":
		case "NOT_IN_RELATION":
			return Array.isArray(value) && value.length > 0 ? null : t("Field is required");
		default:
			return null;
	}
}

function getFilterValueFieldForTag(fieldName: string, operator: string, t: TFunction) {
	switch (operator) {
		case "=":
		case "!=":
			return <TagSingleSelectField name={fieldName} label={t("Tag")} />;
		case "IN_RELATION":
		case "NOT_IN_RELATION":
			return <TagMultiSelectField name={fieldName} label={t("Tags")} />;
		default:
			return null;
	}
}

const mapToRequest = ({ parent, ...company }: ICompanyFormState): ICompanyRequest => ({
	...company,
	parentId: parent?.id ?? null,
});

export const CompanyList = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { sidebarData } = useSidebarData();
	const queryClient = useQueryClient();
	const { createCompany, updateCompany } = useCompany();

	const canCreate = useHasPermission<PermissionName>(["company.create"]);
	const canUpdate = useHasPermission<PermissionName>(["company.update"]);
	const canArchive = useHasPermission<PermissionName>(["company.archive"]);

	const {
		clickHandler: archiveClickHandler,
		drawerProps,
		confirmationProps,
	} = useArchiveByTenantHandler(companyService);

	const dataListColumns: IDrawerDataListColumn<ICompanyIndexResource>[] = useMemo(() => {
		const elements: (IDrawerDataListColumn<ICompanyIndexResource> | null)[] = [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
				sortable: true,
			},
			{
				heading: t("Address"),
				valueKey: "primaryAddress",
				type: "value",
				render: (company) => (company.primaryAddress ? <Address address={company.primaryAddress} /> : "-"),
			},
			{
				heading: t("Tags"),
				valueKey: "tags",
				type: "value",
				render: ({ tags }) => tags.map((tag) => tag.name).join(", "),
				filter: {
					operators: ["IS_EMPTY", "IS_NOT_EMPTY", "=", "!=", "IN_RELATION", "NOT_IN_RELATION"],
					renderActiveColumnFilterValue: (value) => getFilterValueForTag(value),
					renderValueField: ({ fieldName, operator }) => getFilterValueFieldForTag(fieldName, operator, t),
					validateFilter: (value, operator) => validateTagFilter(operator, value, t),
				},
			},
			sidebarData?.applications.carFleets.length || 0 > 0
				? {
						heading: t("Car fleet status"),
						valueKey: "carFleetStatus",
						type: "value",
						render: ({ carFleetStatus }) => t(carFleetStatus),
						filter: {
							operators: ["=", "!="],
							renderActiveColumnFilterValue: (value: CarFleetStatus) => t(value),
							renderValueField: ({ fieldName }) => <CarFleetStatusSelect name={fieldName} />,
							validateFilter: (value) => (value ? null : t("Field is required")),
						},
					}
				: null,
			{
				heading: t("Event priority"),
				valueKey: "eventPriority",
				type: "value",
				filter: {
					operators: ["IS_EMPTY", "IS_NOT_EMPTY", "=", "!="],
					renderActiveColumnFilterValue: (value: string | null) => value,
					renderValueField: ({ fieldName, operator }) =>
						operator === "=" || operator === "!=" ? <EventPrioritySelect name={fieldName} /> : null,
					validateFilter: (value, operator) =>
						(operator === "=" || operator === "!=") && !value ? t("Field is required") : null,
				},
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem title={t("Edit")} label={t("Edit")} action={openDrawer} icon="edit" shortcut="primary" />
						<ContextMenuLinkItem
							to={`${id}/details`}
							title={t("View")}
							label={t("View")}
							icon="eye"
							shortcut="primary"
						/>
						{canArchive && (
							<ContextMenuItem
								action={() => archiveClickHandler(id)}
								title={t("Archive")}
								label={t("Archive")}
								icon="inventory"
							/>
						)}
					</ContextMenu>
				),
			},
		];

		return elements.filter(Boolean) as IDrawerDataListColumn<ICompanyIndexResource>[];
	}, [archiveClickHandler, canArchive, sidebarData?.applications.carFleets.length, t]);

	const handleCreateCompany = useCallback(
		async (request: CompanyCreateRequest) => {
			const response = await createCompany(request);

			if (response !== false) {
				void queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes("companies") });
			}
			return response !== false;
		},
		[createCompany, queryClient],
	);

	return (
		<>
			<PaginatedDrawerAutoDataList
				canCreate={canCreate}
				canUpdate={canUpdate}
				columns={dataListColumns}
				listFetchConfig={laravelPaginatedListFetchConfig(companyService.listKey, companyService.index)}
				renderCreateDrawer={(drawerProps) => <CreateCompanyDrawer {...drawerProps} onSubmit={handleCreateCompany} />}
				renderUpdateDrawer={(drawerProps) => (
					<CompanyUpdateDrawer
						{...drawerProps}
						onSubmit={(entityFormState) => updateCompany(drawerProps.id, mapToRequest(entityFormState))}
					/>
				)}
				translations={{
					title: t("Companies"),
					empty: t("No company available."),
				}}
				onRowClick={({ id }) => navigate(`${id}`)}
				initialSearch={{ search: "", searchFields: [{ name: "name", label: t("Name") }] }}
				initialSorting={{ column: "name", direction: "asc" }}
				enableBulkEdit={true}
				bulkEditActions={<CompanyListBulkActions />}
			/>
			<TenantArchiveDrawer {...drawerProps} title={t("Archive company")} type="archive" service={companyService} />
			<ConfirmationDialog
				{...confirmationProps}
				heading={t("Archive")}
				labels={{ close: t("Close"), confirm: t("Confirm") }}
			>
				{t("Are you sure you want to archive this company?")}
			</ConfirmationDialog>
		</>
	);
};
